import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Copy, KeyRound, Link2, PlugZap, Send, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "../../components/ui/sonner";
import { useStudioAuth } from "../StudioAuthContext";
import { useChaserStream } from "../ChaserStreamContext";
import { EmptyState, FieldLabel, OPS_INPUT, Panel, StatusChip } from "../ui";

export const ChasersSection = () => {
  const { request } = useStudioAuth();
  const { stream, session, state, connect, disconnect } = useChaserStream();
  const [sessions, setSessions] = useState([]);
  const [code, setCode] = useState("");
  const videoRef = useRef(null);

  const load = useCallback(async () => {
    const [sessionsResponse, settingsResponse] = await Promise.all([
      request("/operations/chase/sessions"),
      request("/operations/chase/settings"),
    ]);
    if (sessionsResponse.ok) setSessions(await sessionsResponse.json());
    if (settingsResponse.ok) setCode((await settingsResponse.json()).code || "");
  }, [request]);

  useEffect(() => { load(); const timer = setInterval(load, 5000); return () => clearInterval(timer); }, [load]);
  useEffect(() => { if (videoRef.current) videoRef.current.srcObject = stream || null; }, [stream]);

  const chaseUrl = `${window.location.origin}/chase`;

  const saveCode = async () => {
    const response = await request("/operations/chase/settings", { method: "POST", body: JSON.stringify({ code }) });
    if (response.ok) toast.success("Access code updated");
    else toast.error("Code must be at least 4 characters");
  };

  const sendToPreview = async () => {
    const response = await request("/broadcast", { method: "POST", body: JSON.stringify({ preview_scene: "chaser_cam", lower_third_title: (session?.name || "Chaser").toUpperCase(), lower_third_subtitle: "Live field camera" }) });
    if (response.ok) toast.success("Chaser camera staged in Preview");
    else toast.error("Could not stage the chaser");
  };

  const close = async (sessionId) => {
    await request(`/operations/chase/sessions/${sessionId}/close`, { method: "POST" });
    if (session?.id === sessionId) disconnect();
    load();
  };

  return (
    <div className="grid gap-3 xl:grid-cols-[1fr_340px]">
      <Panel
        title="Field Monitor"
        icon={Camera}
        note={session ? `${session.name} · ${state}` : "No chaser connected"}
        actions={
          <>
            <StatusChip state={state === "live" ? "live" : state === "failed" ? "error" : "idle"} testId="chaser-monitor-status">
              {state === "live" ? "Receiving" : state === "connecting" ? "Connecting" : state === "failed" ? "Failed" : "Idle"}
            </StatusChip>
            {stream && (
              <button data-testid="chaser-send-to-preview-button" onClick={sendToPreview} className="mono flex h-7 items-center gap-1.5 bg-[#1a5cff] px-2.5 text-[9px] font-bold uppercase tracking-widest text-white hover:bg-[#2a6cff]">
                <Send className="h-3.5 w-3.5" /> To Preview
              </button>
            )}
          </>
        }
        bodyClass="p-3"
      >
        <div className="relative aspect-video overflow-hidden border border-white/10 bg-black">
          <video ref={videoRef} data-testid="chaser-studio-video" autoPlay playsInline muted className="h-full w-full object-contain" />
          {!stream && (
            <div className="absolute inset-0 grid place-items-center text-center">
              <div>
                <Camera className="mx-auto h-7 w-7 text-slate-700" />
                <div className="mono mt-2 text-[10px] uppercase tracking-widest text-slate-600">
                  {state === "failed" ? "Direct connection failed — network needs a TURN relay" : "Connect a chaser to see their camera"}
                </div>
              </div>
            </div>
          )}
        </div>
        {state === "failed" && (
          <p className="mono mt-2 text-[9px] uppercase tracking-widest text-amber-300">Peer-to-peer blocked on this network. Ask the chaser to switch to cellular data.</p>
        )}
      </Panel>

      <div className="space-y-3">
        <Panel title="Connected Chasers" icon={PlugZap} note={`${sessions.length} waiting/live`} bodyClass="p-0">
          {sessions.length === 0 ? (
            <EmptyState icon={PlugZap} title="No chasers online" message="Share the link and code below. Chasers appear here the moment they tap Go Live." testId="chasers-empty-state" />
          ) : (
            <div className="divide-y divide-[rgba(120,170,255,0.12)]">
              {sessions.map((item) => (
                <div key={item.id} data-testid={`chaser-session-${item.id}`} className="flex items-center justify-between gap-2 px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-bold text-white">{item.name}</div>
                    <div className="mono text-[9px] uppercase tracking-widest text-slate-600">{item.state}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button data-testid={`chaser-connect-${item.id}`} onClick={() => connect(item)} className="mono h-7 border border-sky-500/30 px-2 text-[9px] font-bold uppercase tracking-widest text-sky-300 hover:bg-sky-500/10">
                      {session?.id === item.id ? "Reconnect" : "Receive"}
                    </button>
                    <button data-testid={`chaser-close-${item.id}`} onClick={() => close(item.id)} title="Drop chaser" className="grid h-7 w-7 place-items-center border border-white/10 text-slate-500 hover:border-red-500/30 hover:text-red-400">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Chaser Link" icon={Link2} bodyClass="space-y-3 p-4">
          <div className="space-y-1">
            <FieldLabel>Shared link</FieldLabel>
            <div className="flex gap-1">
              <Input readOnly value={chaseUrl} data-testid="chaser-link-input" className={`${OPS_INPUT} mono text-xs`} />
              <button
                data-testid="chaser-copy-link-button"
                onClick={() => { navigator.clipboard?.writeText(`${chaseUrl} · code ${code}`); toast.success("Link and code copied"); }}
                className="grid h-9 w-9 shrink-0 place-items-center border border-white/10 text-slate-400 hover:bg-white/5 hover:text-white"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <FieldLabel>Access code</FieldLabel>
            <div className="flex gap-1">
              <Input value={code} onChange={(event) => setCode(event.target.value.toUpperCase())} data-testid="chaser-code-setting-input" className={`${OPS_INPUT} mono tracking-widest`} />
              <Button data-testid="chaser-save-code-button" onClick={saveCode} className="h-9 shrink-0 rounded-none bg-[#1a5cff] px-3 text-[10px] font-bold uppercase tracking-widest hover:bg-[#2a6cff]">
                <KeyRound className="h-3.5 w-3.5" /> Save
              </Button>
            </div>
          </div>
          <p className="mono text-[9px] uppercase leading-relaxed tracking-wider text-slate-600">Chasers open the link on a phone, enter the code, and appear above. Video is peer-to-peer to this studio only.</p>
        </Panel>
      </div>
    </div>
  );
};

export default ChasersSection;
